import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import { Helmet } from "react-helmet";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "./styles.css";
import { companies } from "./AppraisalReportForm";
import { pdf } from "@react-pdf/renderer";
import { useNavigate } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { Link } from "react-router-dom";
import MyDocument from "./MyDocument"; // PDF component
import InputMask from "react-input-mask";
import { API_BASE_URL } from "../config/config";
import { NumericFormat } from "react-number-format";
import {
  selectedCompany,
  calculateHighFinalValues,
  calculateLowFinalValues,
  calculateALowValue,
  calculateBLowValue,
  calculateCLowValue,
  calculateMaxLossrange,
  calculateLossThresold,
  calculateDamageSeverityScore,
  calculateGrade,
  calculatePostRepairFMV,
  calculateInherentDV,
  calculateDiminishedValue,
  calculateMaxLossPercentage,
  calculateDamageToValue,
} from "./utils/formulas"; // Import additional formulas

const EditForm = () => {
  const { id } = useParams();
  const [form, setForm] = useState(null);

  const handleSave = async () => {
    const { selectedCompany } = formData; // Ensure the selected company is correct
    const companyLogoPng = companies[selectedCompany]?.logoPng; // Get the PNG version

    console.log("Selected Company:", selectedCompany); // Debugging
    console.log("Company Logo PNG:", companyLogoPng); // Debugging

    if (!companyLogoPng) {
      console.error("Error: No PNG logo found for the selected company.");
      return;
    }

    try {
      // Exclude _id from the data to be sent
      const { _id, ...dataToUpdate } = formData;

      // Include checkbox data if necessary
      const updatedFormData = {
        ...dataToUpdate,
        checkboxes,
        companyLogo: companyLogoPng, // Ensure the PNG logo is included
      };

      // Generate the PDF blob
      const blob = await pdf(<MyDocument formData={updatedFormData} chartImage={chartImage} companyLogo={companyLogoPng} />).toBlob();

      // Create a FormData object to send the form data and the PDF
      const formDataToSend = new FormData();
      formDataToSend.append("pdfFile", blob, `${formData.vin}_${formData.dateOfLoss}.pdf`);
      formDataToSend.append("formData", JSON.stringify(updatedFormData));

      // Make the PUT request to update the form and upload the new PDF
      const response = await fetch(`${API_BASE_URL}/api/forms/${_id}`, {
        method: "PUT",
        body: formDataToSend, // Send FormData with the PDF and form data
      });

      if (response.ok) {
        const updatedForm = await response.json();
        console.log("Form updated successfully:", updatedForm);
        alert("Form updated successfully!"); // Display success alert
      } else {
        const errorResponse = await response.json();
        console.error("Error response from server:", errorResponse);
      }
    } catch (error) {
      console.error("Error saving form data:", error);
      alert("An error occurred while updating the form. Please try again."); // Display error alert
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Update form data dynamically
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value, // Set the field dynamically
    }));
  };

  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    vehicleOwner: "",
    fileNumber: "",
    dateOfLoss: "",
    insuranceCo: "",
    claimNumber: "",
    claimType: "First Party Claim",
    vin: "",
    odometerReading: "",
    yearMakeModel: "",
    laborHours: "",
    repairCost: "",
    structuralDamage: "",
    airbagDeployment: "",
    priorLoss: "",
    uploadBlackBook: "",
    fmv_high: "",
    a_high: 0,
    b_high: 0,
    c_high: 0,
    final_high: "",
    fmv_low: "",
    a_low: "",
    b_low: "",
    c_low: "",
    a_type: "",
    b_type: "",
    c_type: "",
    final_low: "",
    max_loss: "",
    max_loss_percentage: "", // New field for max loss percentage
    valueDifference: "",
    loss_thresold: "",
    damage_to_value: "",
    PreLossFMV: "",
    inherentDV: "",
    postRepairFMV: "",
    diminishedValue: "",
    damageScore: "",
    grade: "",
    selectedCompany: "",
  });

  // Consolidated form data fetch
  useEffect(() => {
    const fetchData = async () => {
      console.log("Fetching form with ID:", id);

      try {
        const response = await fetch(`${API_BASE_URL}/api/forms/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Fetched form data:", data);

        // Update both formData and checkboxes states
        setFormData({
          ...data,
          vehicleOwner: data.vehicleOwner || "",
          fileNumber: data.fileNumber || "",
          dateOfLoss: data.dateOfLoss || "",
          insuranceCo: data.insuranceCo || "",
          claimNumber: data.claimNumber || "",
          claimType: data.claimType || "First Party Claim",
          vin: data.vin || "",
          yearMakeModel: data.yearMakeModel || "",
          odometerReading: data.odometerReading || "",
          laborHours: data.laborHours || "",
          repairCost: data.repairCost || "",
          structuralDamage: data.structuralDamage || "no",
          airbagDeployment: data.airbagDeployment || "no",
          priorLoss: data.priorLoss || "no",
          uploadBlackBook: data.uploadBlackBook || "",
          fmv_high: data.fmv_high || "",
          a_high: data.a_high || 0,
          b_high: data.b_high || 0,
          c_high: data.c_high || 0,
          fmv_low: data.fmv_low || "",
          a_low: data.a_low || "",
          b_low: data.b_low || "",
          c_low: data.c_low || "",
          final_low: data.final_low || "",
          a_type: data.a_type || "",
          b_type: data.b_type || "",
          c_type: data.c_type || "",
          finalHighValue: data.finalHighValue || "",
          max_loss: data.max_loss || "",
          damage_to_value: data.damage_to_value || "",
          damageScore: data.damageScore || "",
          grade: data.grade || "",
          preLossFMV: data.preLossFMV || "",
          inherentDV: data.inherentDV || "",
          postRepairFMV: data.postRepairFMV || "",
          selectedCompany: data.selectedCompany || "ae", // Default to 'ae'
        });

        // Explicitly set checkboxes state
        setCheckboxes({
          checkbox1: data.checkbox1 || false,
          checkbox2: data.checkbox2 || false,
          checkbox3: data.checkbox3 || false,
          checkbox4: data.checkbox4 || false,
          checkbox5: data.checkbox5 || false,
          checkbox6: data.checkbox6 || false,
          checkbox7: data.checkbox7 || false,
          checkbox8: data.checkbox8 || false,
          checkbox9: data.checkbox9 || false,
          checkbox10: data.checkbox10 || false,
          checkbox11: data.checkbox11 || false,
          checkbox12: data.checkbox12 || false,
          checkbox13: data.checkbox13 || false,
          checkboxUnder: data.checkboxUnder || false,
          checkboxFire: data.checkboxFire || false,
          checkboxWater: data.checkboxWater || false,
          checkboxPdr: data.checkboxPdr || false,
          checkboxOther: data.checkboxOther || false,
          yes1: data.yes1 || false,
          yes2: data.yes2 || false,
          yes3: data.yes3 || false,
          yes4: data.yes4 || false,
          yes5: data.yes5 || false,
          yes6: data.yes6 || false,
          checkboxYes: data.checkboxYes || false,
        });
      } catch (error) {
        console.error("Error fetching form data:", error);
      }
    };

    fetchData();
  }, [id]);

  // Chart image update
  useEffect(() => {
    if (chartRef.current && chartRef.current.toBase64Image) {
      const base64Image = chartRef.current.toBase64Image();
      setChartImage(base64Image);
    }
  }, [formData.finalHighValue, formData.postRepairFMV, formData.diminishedValue]);

  useEffect(() => {
    if (formData.selectedCompany) {
      const logoPng = companies[formData.selectedCompany]?.logoPng;
      setFormData((prevFormData) => ({
        ...prevFormData,
        companyLogo: logoPng, // Ensure PNG logo is set
      }));
    }
  }, [formData.selectedCompany]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/forms/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        console.log("Fetched form data:", data);

        setFormData({
          ...data,
          isDamageScoreEdited: false, // Reset flag
        });
      } catch (error) {
        console.error("Error fetching form data:", error);
      } finally {
        setLoading(false); // Stop loading once the fetch completes
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    console.log("Damage Score after fetching:", formData.damageScore);
  }, [formData]);

  const [chartImage, setChartImage] = useState(""); // State to store chart image as base64
  const [isPDFReady, setIsPDFReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false); // Estado para o menu suspenso
  const chartRef = useRef(null); // Reference for the chart
  const [pdfBlob, setPdfBlob] = useState(null);
  const [showPreview, setShowPreview] = useState(false); // Control the preview visibility

  const navigate = useNavigate();

  const [previewImageSrc, setPreviewImageSrc] = useState("/previewButtonHeader.svg");
  const [savedFormsImageSrc, setSavedFormsImageSrc] = useState("/savedFormsButtomHeader.svg");
  const [saveButtonImageSrc, setSaveButtonImageSrc] = useState("/saveButtonHeader.svg");

  const isValidChartImage = (image) => {
    return image && image.startsWith("data:image/png;base64,");
  };

  // Function to calculate the damage to value ratio
  const calculateDamageToValue = (repairCost, finalHighValue) => {
    if (finalHighValue > 0) {
      return ((repairCost / finalHighValue) * 100).toFixed(0); // Return the percentage as a number
    }
    return ""; // Return empty if finalHighValue is zero or undefined
  };

  // THESE ARE THE CHECKBOXES
  const [checkboxes, setCheckboxes] = useState({
    checkbox1: true,
    checkbox2: false,
    checkbox3: false,
    checkbox4: false,
    checkbox5: false,
    checkbox6: false,
    checkbox7: false,
    checkbox8: false,
    checkbox9: false,
    checkbox10: false,
    checkbox11: false,
    checkbox12: false,
    checkbox13: false,
    checkboxUnder: false,
    checkboxFire: false,
    checkboxWater: false,
    checkboxPdr: false,
    checkboxOther: false,
    yes1: false,
    yes2: false,
    yes3: false,
    yes4: false,
    yes5: false,
    yes6: false,
    checkboxYes: false,
  });

  const [loading, setLoading] = useState(true); // Add this lin

  const handleDamageScoreChange = (value) => {
    // Ensure the value is a number or defaults to 0
    const parsedValue = parseFloat(value) || 0;

    setFormData((prevFormData) => {
      const updatedFormData = { ...prevFormData, damageScore: parsedValue };

      // Trigger recalculation of dependent values
      const structural = updatedFormData.structuralDamage === "yes";
      const airbag = updatedFormData.airbagDeployment === "yes";
      const laborHours = parseFloat(updatedFormData.laborHours) || 0;
      const repairCost = parseFloat(updatedFormData.repairCost) || 0;
      const damageToValue = calculateDamageToValue(repairCost, updatedFormData.final_high);

      console.log("Updated FormData in handleDamageScoreChange:", updatedFormData);

      // Only recalculate other values if they depend on factors other than damageScore
      const inherentDV = calculateInherentDV(updatedFormData.max_loss, parsedValue);
      const postRepairFMV = calculatePostRepairFMV(updatedFormData.PreLossFMV, inherentDV);
      const diminishedValue = calculateDiminishedValue(updatedFormData.final_high, postRepairFMV);

      // Calculate the grade using your provided logic
      const grade = calculateGrade(parsedValue);

      return {
        ...updatedFormData,
        inherentDV: inherentDV.toFixed(0),
        postRepairFMV: postRepairFMV.toFixed(0),
        diminishedValue: diminishedValue.toFixed(0),
        grade, // Update the grade based on damage score
      };
    });
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;

    // Update the formData state to reflect the selected radio button's value
    setFormData((prevFormData) => {
      const updatedFormData = { ...prevFormData, [name]: value };

      // Recalculate the values based on the updated formData
      const structural = updatedFormData.structuralDamage !== "None";
      const airbag = updatedFormData.airbagDeployment === "yes";
      const laborHours = parseFloat(updatedFormData.laborHours) || 0;
      const repairCost = parseFloat(updatedFormData.repairCost) || 0;
      const damageToValue = calculateDamageToValue(repairCost, updatedFormData.final_high);

      const calculatedDamageScore = calculateDamageSeverityScore(laborHours, repairCost, damageToValue / 100, structural, airbag);

      return {
        ...updatedFormData,
        damage_to_value: damageToValue,
        damageScore: calculatedDamageScore.toFixed(0),
      };
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;

    // Update checkboxes state
    setCheckboxes((prevCheckboxes) => ({
      ...prevCheckboxes,
      [name]: checked,
    }));

    // Update formData (if needed)
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: checked,
    }));
  };

  const handleInputChange = (e) => {
    const applySign = (value, sign) => {
      const numeric = parseFloat(value) || 0;
      return sign === "-" ? -Math.abs(numeric) : Math.abs(numeric);
    };

    const { name, value, type } = e.target;

    // If the field is a checkbox, handle its state
    if (type === "checkbox") {
      setCheckboxes((prevCheckboxes) => {
        const updatedCheckboxes = { ...prevCheckboxes };

        // Toggle the checkbox state
        updatedCheckboxes[name] = !prevCheckboxes[name];

        // Handle checkbox-specific logic
        if (updatedCheckboxes[name]) {
          if (name === "yes1" || name === "yes2") {
            updatedCheckboxes.yes1 = name === "yes1";
            updatedCheckboxes.yes2 = name === "yes2";
            setFormData((prevFormData) => ({
              ...prevFormData,
              structuralDamage: name === "yes1" ? "yes" : "no",
            }));
          }
          if (name === "yes3" || name === "yes4") {
            updatedCheckboxes.yes3 = name === "yes3";
            updatedCheckboxes.yes4 = name === "yes4";
            setFormData((prevFormData) => ({
              ...prevFormData,
              airbagDeployment: name === "yes3" ? "yes" : "no",
            }));
          }
          if (name === "yes5" || name === "yes6") {
            updatedCheckboxes.yes5 = name === "yes5";
            updatedCheckboxes.yes6 = name === "yes6";
            setFormData((prevFormData) => ({
              ...prevFormData,
              priorLoss: name === "yes5" ? "yes" : "no",
            }));
          }
        }

        return updatedCheckboxes;
      });
    } else {
      // If the field is not a checkbox, handle other inputs
      setFormData((prevFormData) => {
        const isNumericField = ["fmv_high", "fmv_low", "a_high", "a_low", "b_high", "b_low", "c_high", "c_low", "repairCost", "laborHours"].includes(name);

        // Ensure one decimal after the dot, and handle deletion correctly
        let updatedValue = value;

        // Only allow one decimal
        if (isNumericField) {
          // Remove anything that is not a number or a dot, then limit to one dot
          updatedValue = value.replace(/[^0-9.]/g, ""); // Allow numbers and decimal point only
          if (updatedValue.includes(".")) {
            // Keep only the first occurrence of the decimal point
            updatedValue = updatedValue.split(".").shift() + "." + updatedValue.split(".").slice(1).join("");
          }
        }

        // If empty, reset to an empty string
        if (updatedValue === "") {
          updatedValue = "";
        }

        const updatedFormData = {
          ...prevFormData,
          [name]: updatedValue,
        };

        if (["a_sign", "b_sign", "c_sign"].includes(name)) {
          updatedFormData[name] = value;
          if (name === "a_sign" && prevFormData.a_high !== undefined) {
            updatedFormData.a_high = String(prevFormData.a_high);
          }
          if (name === "b_sign" && prevFormData.b_high !== undefined) {
            updatedFormData.b_high = String(prevFormData.b_high);
          }
          if (name === "c_sign" && prevFormData.c_high !== undefined) {
            updatedFormData.c_high = String(prevFormData.c_high);
          }
        }

        // Log current form data for debugging
        console.log("Current Form Data:", updatedFormData);

        
        const adjustedBLow = applySign(parseFloat(updatedFormData.b_low) || 0, updatedFormData.b_sign);
        const adjustedCLow = applySign(parseFloat(updatedFormData.c_low) || 0, updatedFormData.c_sign);
        
        const b_low = adjustedBLow;
        const c_low = adjustedCLow;
        
        
        const adjustedAHigh = applySign(updatedFormData.a_high, updatedFormData.a_sign);
        const adjustedBHigh = applySign(updatedFormData.b_high, updatedFormData.b_sign);
        const adjustedCHigh = applySign(updatedFormData.c_high, updatedFormData.c_sign);
        
        const a_low = calculateALowValue(updatedFormData.fmv_high, adjustedAHigh, updatedFormData.fmv_low);

        const highFinalValuesResult = calculateHighFinalValues(
          updatedFormData.fmv_high,
          adjustedAHigh, // Use adjusted value for A
          adjustedBHigh, // Use adjusted value for B
          adjustedCHigh // Use adjusted value for C
        );

        const lowFinalValuesResult = calculateLowFinalValues(updatedFormData.fmv_low, a_low, b_low, c_low);

        const preLossFMV = highFinalValuesResult;

        const maxLossRange = calculateMaxLossrange(updatedFormData.fmv_high, adjustedAHigh, adjustedBHigh, adjustedCHigh, updatedFormData.fmv_low, a_low, b_low, c_low);

        const lossThreshold = calculateLossThresold(updatedFormData.fmv_high, adjustedAHigh, adjustedBHigh, adjustedCHigh);

        const maxLossPercentage = calculateMaxLossPercentage(maxLossRange, highFinalValuesResult) * 100;

        // Log the max loss percentage for debugging
        console.log(`Max Loss Range: ${maxLossRange}`);
        console.log(`High Final Values: ${highFinalValuesResult}`);
        console.log(`Max Loss Percentage: ${maxLossPercentage.toFixed(0)}%`);

        const damageToValue = calculateDamageToValue(updatedFormData.repairCost, highFinalValuesResult);

        const structural = updatedFormData.structuralDamage !== "None";
        const airbag = updatedFormData.airbagDeployment === "yes";
        const laborHours = parseFloat(updatedFormData.laborHours) || 0;
        const repairCost = parseFloat(updatedFormData.repairCost) || 0;
        const calculatedDamageScore = calculateDamageSeverityScore(laborHours, repairCost, damageToValue / 100, structural, airbag);

        const inherentDV = calculateInherentDV(maxLossRange, calculatedDamageScore);
        const postRepairFMV = calculatePostRepairFMV(preLossFMV, inherentDV);
        const finalHighValue = highFinalValuesResult;
        const diminishedValue = calculateDiminishedValue(finalHighValue, postRepairFMV);

        const calculatedGrade = calculateGrade(calculatedDamageScore);

        console.log("🔥 Debug - FINAL CALC STAGE 🔥");
        console.log("Final High Value (parsed):", finalHighValue);
        console.log("Post Repair FMV:", postRepairFMV);
        console.log("Expected DV:", finalHighValue - postRepairFMV);
        console.log("Pre-Loss FMV:", preLossFMV);
        console.log("Inherent DV:", inherentDV);
        console.log("Max Loss Range:", maxLossRange);
        console.log("Calculated Damage Score:", calculatedDamageScore);
        console.log(">>> adjustedAHigh:", adjustedAHigh);
        console.log(">>> adjustedBHigh:", adjustedBHigh);
        console.log(">>> adjustedCHigh:", adjustedCHigh);
        console.log(">>> High FMV:", updatedFormData.fmv_high);
        console.log(">>> Final High CALC INPUTS:", adjustedAHigh, adjustedBHigh, adjustedCHigh);

        return {
          ...updatedFormData,
          a_low,
          b_low,
          c_low,
          final_high: highFinalValuesResult,
          PreLossFMV: preLossFMV,
          final_low: lowFinalValuesResult,
          max_loss: maxLossRange,
          max_loss_percentage: maxLossPercentage.toFixed(0), // Stored as a separate field for max loss percentage
          valueDifference: highFinalValuesResult - lowFinalValuesResult,
          loss_thresold: lossThreshold,
          damage_to_value: damageToValue,
          damageScore: calculatedDamageScore.toFixed(0),
          grade: calculatedGrade,
          inherentDV: inherentDV.toFixed(0),
          postRepairFMV: postRepairFMV.toFixed(0),
          diminishedValue: diminishedValue.toFixed(0),
        };
      });
    }
  };

  const [fileTag, setFileTag] = useState(""); // State to store the file name
  const [isDragging, setIsDragging] = useState(false); // State to handle dragging effect

  // Function to handle file uploads
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log(`File selected: ${file.name}, Type: ${file.type}`); // Log file name and type

      // Validate MIME type
      if (file.type.startsWith("image/")) {
        setErrorMessage(""); // Clear any previous error messages
        const fileURL = URL.createObjectURL(file);

        // Update form data and display the selected file
        setFormData((prev) => ({
          ...prev,
          uploadBlackBook: fileURL,
        }));
        setFileTag(file.name);
        handleFileUpload(file); // Proceed with upload
      } else {
        console.log(`Invalid file type: ${file.type}`); // Log invalid file type
        // Show error message and reset input
        setErrorMessage("Invalid file format. Only images are allowed.");
        e.target.value = null; // Clear the file input to prevent invalid files from being uploaded
        setFormData((prev) => ({
          ...prev,
          uploadBlackBook: "", // Clear the form data related to the invalid file
        }));
        setFileTag(""); // Clear the displayed file tag
      }
    }
  };

  const handleFileUpload = (file) => {
    if (file) {
      setFileTag(file.name); // Store the file name
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prevFormData) => ({
          ...prevFormData,
          uploadBlackBook: reader.result, // Store the file as a base64 string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      console.log(`File dropped: ${file.name}, Type: ${file.type}`); // Log file name and type

      // Validate MIME type
      if (file.type.startsWith("image/")) {
        setErrorMessage(""); // Clear any previous error messages
        const fileURL = URL.createObjectURL(file);

        // Update form data and display the selected file
        setFormData((prev) => ({
          ...prev,
          uploadBlackBook: fileURL,
        }));
        setFileTag(file.name);
        handleFileUpload(file); // Proceed with upload
      } else {
        console.log(`Invalid file type: ${file.type}`); // Log invalid file type
        // Show error message and prevent file upload
        setErrorMessage("Invalid file format. Only images are allowed.");
        setFormData((prev) => ({
          ...prev,
          uploadBlackBook: "", // Clear the form data related to the invalid file
        }));
        setFileTag(""); // Clear the displayed file tag
      }
    }
  };

  // Function to clear the form
  const clearForm = () => {
    setFormData({
      vehicleOwner: "",
      fileNumber: "",
      dateOfLoss: "",
      insuranceCo: "",
      claimNumber: "",
      claimType: "",
      vin: "",
      odometerReading: "",
      yearMakeModel: "",
      laborHours: "",
      repairCost: "",
      structuralDamage: "",
      airbagDeployment: "",
      priorLoss: "",
      uploadBlackBook: "",
      fmv_high: "",
      a_high: "",
      b_high: "",
      c_high: "",
      final_high: "",
      fmv_low: "",
      a_low: "",
      b_low: "",
      c_low: "",
      final_low: "",
      max_loss: "",
      valueDifference: "",
      loss_thresold: "",
      damage_to_value: "",
      PreLossFMV: "",
      inherentDV: "",
      postRepairFMV: "",
      diminishedValue: "",
      damageScore: "",
      grade: "",
    });
    setIsPDFReady(false);
  };

  const chartData = {
    labels: ["Damage Score", "Diminished Value"],
    datasets: [
      {
        label: "Values",
        data: [formData.damageScore || 0, formData.diminishedValue || 0], // Ensure numeric values
        backgroundColor: ["#FF6384", "#36A2EB"],
      },
    ],
  };

  const ChartComponent = ({ finalHighValue = 0, postRepairFMV = 0, diminishedValue = 0, setChartImage, textColor = "#000000" }) => {
    const chartRef = useRef(null);
    const svgImagesRef = useRef([]); // Store loaded images

    // 🔹 Load Arrow Images Once
    useEffect(() => {
      const svgUrls = ["/greenArrow.png", "/blueArrow.png", "/orangeArrow.png"];

      let imagesLoadedCount = 0;
      svgUrls.forEach((url, index) => {
        const img = new Image();
        img.src = url;
        img.onload = () => {
          console.log(`✅ Loaded image: ${img.src}`);
          svgImagesRef.current[index] = img;
          imagesLoadedCount++;

          if (imagesLoadedCount === svgUrls.length) {
            console.log("✅ All images loaded successfully");

            // 🔥 Force chart update so arrows appear
            if (chartRef.current) {
              console.log("🔄 Updating chart...");
              chartRef.current.update();
            }
          }
        };

        img.onerror = () => console.error(`❌ Failed to load image: ${url}`);
      });
    }, []);

    // 🔥 Arrow Plugin (Fixes Position Tracking)
    const arrowPlugin = {
      id: "arrowPlugin",
      afterDatasetsDraw: (chart) => {
        const ctx = chart.ctx;
        ctx.save();

        chart.data.datasets.forEach((_, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          meta.data.forEach((bar, index) => {
            if (!bar) return;

            // ✅ Get bar's tooltip position (most accurate method)
            const { x: barX, y: barY } = bar.tooltipPosition();
            console.log(`🟢 Bar ${index} Position -> X=${barX}, Y=${barY}`);

            if (svgImagesRef.current[index]) {
              const img = svgImagesRef.current[index];

              // ✅ Glue arrow **under** the label container
              const arrowWidth = 40;
              const arrowHeight = 20;
              const arrowX = barX - arrowWidth / 2;
              const arrowY = barY - 40; // 🔥 Adjust dynamically so arrow follows

              console.log(`📌 Drawing Arrow ${index} -> X=${arrowX}, Y=${arrowY}`);
              ctx.drawImage(img, arrowX, arrowY, arrowWidth, arrowHeight);
            } else {
              console.log(`⚠️ Arrow ${index} image not ready!`);
            }
          });
        });

        ctx.restore();
      },
    };

    useEffect(() => {
      if (chartRef.current && chartRef.current.toBase64Image) {
        console.log("🔄 Waiting for chart update...");

        setTimeout(() => {
          if (chartRef.current) {
            console.log("📌 Chart is ready, capturing image...");
            const base64Image = chartRef.current.toBase64Image();

            if (isValidChartImage(base64Image)) {
              console.log("✅ Chart image is valid, setting state...");
              setChartImage(base64Image);
            } else {
              console.warn("⚠️ Chart image is invalid, retrying...");
            }
          } else {
            console.warn("⚠️ Chart reference still not ready!");
          }
        }, 200); // 🔥 Small delay ensures chart is fully rendered
      } else {
        console.warn("⚠️ Chart reference is NULL! Retrying...");
      }
    }, [finalHighValue, postRepairFMV, diminishedValue]);

    const data = {
      labels: ["Final High Value", "Post Repair FMV", "Total Diminished Value"],
      datasets: [
        {
          data: [Math.round(finalHighValue), Math.round(postRepairFMV), Math.round(diminishedValue)],
          backgroundColor: ["#62deaa", "#52a0ba", "#ffb578"],
          borderWidth: 0,
          borderRadius: 12,
          barPercentage: 0.7,
          categoryPercentage: 0.8,
        },
      ],
    };

    const maxValue = Math.max(finalHighValue, postRepairFMV, diminishedValue);
    const topPadding = maxValue > 100000 ? 80 : 50;

    const options = {
      responsive: true,
      maintainAspectRatio: true,
      layout: {
        padding: {
          top: topPadding,
          bottom: 35,
        },
      },
      plugins: {
        legend: { display: false },
        datalabels: {
          display: true,
          color: "#ffffff",
          borderRadius: 17,
          padding: { top: 18, bottom: 18, left: 50, right: 50 },
          minWidth: 200,
          maxWidth: 200,
          minHeight: 56,
          textAlign: "center",
          anchor: "end",
          align: "top",
          offset: 40,
          clip: false,
          font: {
            weight: "bold",
            size: 20,
            lineHeight: 1,
            family: "'Gilroy', sans-serif",
          },
          backgroundColor: (context) => {
            const colors = ["#00c274", "#006080", "#fa9943"];
            return colors[context.dataIndex];
          },
          formatter: (value) => {
            let formatted = `$${Math.round(value).toLocaleString("en-US")}`;
            const TARGET_CHARS = 12;
            const clean = formatted.replace(/,/g, "");

            if (clean.length > TARGET_CHARS) {
              return `${clean.slice(0, TARGET_CHARS - 3)}...`.padEnd(TARGET_CHARS, "\u00A0");
            }

            const totalSpaces = TARGET_CHARS - clean.length;
            const leftSpaces = Math.floor(totalSpaces / 2);
            const rightSpaces = totalSpaces - leftSpaces;

            return "\u00A0".repeat(leftSpaces) + formatted + "\u00A0".repeat(rightSpaces);
          },
        },
        tooltip: {
          enabled: true,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          titleFont: { size: 14 },
          bodyFont: { size: 12 },
          callbacks: {
            label: (context) => `$${Math.round(context.raw).toLocaleString("en-US")}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { display: false },
          suggestedMax: maxValue * 1.2,
        },
        x: {
          grid: { display: false },
          ticks: {
            color: "#555",
            font: { size: 16 },
          },
        },
      },
      animation: { duration: 0 },
    };

    return <Bar ref={chartRef} data={data} options={options} plugins={[ChartDataLabels, arrowPlugin]} />;
  };

  console.log("Form Data in handleSubmit:", formData.dateOfLoss); // Check if dateOfLoss has a value

  // Function to generate and upload the PDF, returning the accessible URL
  const pdfGenerationFunction = async (formData) => {
    const { vin, dateOfLoss } = formData;
    const fileName = `${vin}_${dateOfLoss}.pdf`;

    // Generate the PDF blob
    const blob = await pdf(<MyDocument formData={formData} chartImage={chartImage} />).toBlob();

    // Upload the PDF blob to the backend
    const pdfUrl = await uploadPdfToStorage(blob, fileName);

    return pdfUrl;
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Log formData to check its contents
    console.log("Form Data before generating PDF:", formData);
    console.log("VIN:", formData.vin);
    console.log("Date of Loss:", formData.dateOfLoss);

    try {
      // Step 1: Generate the PDF Blob
      const pdfBlob = await pdfGenerationFunction(formData);
      console.log("Generated PDF Blob:", pdfBlob);

      if (!pdfBlob) {
        console.error("PDF Blob is not generated correctly");
        return;
      }

      // Step 2: Create FormData to send to the backend
      const formDataToSend = new FormData();
      formDataToSend.append("pdfFile", pdfBlob, `${formData.vin}_${formData.dateOfLoss}.pdf`); // Attach PDF Blob as 'pdfFile'
      formDataToSend.append("formData", JSON.stringify(formData)); // Attach form data as JSON string

      // Step 3: Always use POST to create a new form
      const response = await fetch(`${API_BASE_URL}/api/forms`, {
        method: "POST",
        body: formDataToSend,
      });

      // Step 4: Handle the server response
      if (response.ok) {
        console.log("Form created successfully");
        const jsonResponse = await response.json();
        console.log("Response from server:", jsonResponse);
        setFormData({}); // Optionally reset form fields after saving
      } else {
        console.error("Failed to create form");
        const errorResponse = await response.json();
        console.error("Error response:", errorResponse);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const uploadPdfToStorage = async (blob, fileName) => {
    const uploadData = new FormData(); // Rename to `uploadData` to avoid conflict
    uploadData.append("pdfFile", blob, fileName); // Attach PDF Blob as 'pdfFile'
    uploadData.append("formData", JSON.stringify(formData)); // Attach form data as JSON

    const response = await fetch(`${API_BASE_URL}/api/forms`, {
      method: "POST",
      body: uploadData,
    });

    if (!response.ok) throw new Error("PDF upload failed");
    const { pdfUrl } = await response.json();
    return pdfUrl;
  };

  const handleGenerateAndDownloadPDF = async () => {
    setIsLoading(true); // Set loading state to true while generating PDF

    // Generate the PDF using the form data
    const blob = await pdf(<MyDocument formData={formData} chartImage={chartImage} />).toBlob();

    // Create a URL for the generated PDF blob and trigger the download
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${formData.vin || "AppraisalReport"}_${formData.dateOfLoss || "NoDate"}.pdf`;
    link.click();

    // Clean up the blob URL and reset the loading state
    URL.revokeObjectURL(url);
    setIsLoading(false);
  };

  const handlePreview = async () => {
    setIsLoading(true); // Indica que a geração está em progresso

    // Gera o PDF apenas se a imagem do gráfico já foi capturada
    if (chartImage) {
      const blob = await pdf(<MyDocument formData={formData} chartImage={chartImage} />).toBlob();
      setPdfBlob(blob); // Define o PDF gerado para pré-visualização
      setShowPreview(true); // Mostra a pré-visualização quando o PDF estiver pronto
    }

    setIsLoading(false); // Indica que a geração terminou
  };

  const closePreview = () => {
    setShowPreview(false); // Fecha a pré-visualização
  };

  const pdfUrl = useMemo(() => {
    if (!pdfBlob) return null;
    return URL.createObjectURL(pdfBlob);
  }, [pdfBlob]); // Only regenerate when pdfBlob changes

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl); // Revoke the object URL when the component is unmounted or pdfUrl changes
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (chartRef.current && chartRef.current.toBase64Image) {
      const base64Image = chartRef.current.toBase64Image();
      setChartImage(base64Image);
    }
  }, [formData.finalHighValue, formData.postRepairFMV, formData.diminishedValue]);

  return (
    <div
      style={{
        background: "linear-gradient(237deg, rgba(39, 39, 39, 0) 70%, rgba(0, 195, 116, 0.4) 120%), #272727",
        backgroundAttachment: "fixed", // This fixes the background in place
        backgroundSize: "cover", // Ensure the background covers the whole page
      }}
    >
      <header>
        <Link to="/">
          <div className="logo">
            <img src="/ae.svg" alt="Logo" className="header-image" />
          </div>
        </Link>

        <div className="actions">
          <button className="export" onClick={handleGenerateAndDownloadPDF} disabled={isLoading}>
            <img className="download-icon" src="/image 4.png" alt="Export" />
            {isLoading ? "Export" : "Export"}
          </button>

          <button className="clear" onClick={() => setFormData({})}>
            Clear
          </button>

          <button className="google-button" onClick={handleSave} type="submit">
            <div className="save-icon" role="img" aria-label="Preview icon" />
          </button>

          <button className="otherButtons" onClick={handlePreview}>
            <div className="preview-icon" role="img" aria-label="Preview icon" />
          </button>

          <button
            className="otherButtons"
            onClick={() => {
              window.scrollTo(0, 0); // Scroll to the top of the page
              navigate("/forms"); // Redirect to /forms
            }}
          >
            <div className="saved-icon" role="img" aria-label="Preview icon" />
          </button>
        </div>
      </header>

      <div className="container">
        <div className="titleDiv">
          <h1 className="bigTitle">Appraisal Report</h1>
          <p className="subTitle">Edit your forms here.</p>
        </div>
        <section className="form-section">
          <h2>Claim Information</h2>
          <form>
            <div className="form-group">
              <label className="titleForInformation" htmlFor="vehicleOwner">
                Vehicle Owner
              </label>
              <input className="vehicleOwner" type="text" name="vehicleOwner" value={formData?.vehicleOwner || ""} onChange={handleChange} placeholder="Full Name" />
            </div>
            <div className="form-group">
              <label className="titleForInformation" htmlFor="fileNumber">
                File Number
              </label>
              <input className="fileNumber" type="text" name="fileNumber" value={formData?.fileNumber || ""} onChange={handleChange} placeholder="#AAAA" />
            </div>
            <div className="form-group">
              <label className="titleForInformation" htmlFor="dateOfLoss">
                Date of Loss
              </label>

              <InputMask
                mask="99/99/9999"
                maskChar={null} // Removes extra mask characters
                value={formData?.dateOfLoss || ""} // Display the date of loss from formData state (editable)
                onFocus={(e) => {
                  // Optionally clear the field when focused, but we use formData here
                  if (!formData.dateOfLoss) {
                    e.target.value = "";
                  }
                }}
                onChange={(e) => {
                  const { value } = e.target;
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    dateOfLoss: value, // Update date of loss in formData when changed
                  }));
                }}
                placeholder="MM/DD/YYYY"
                className="dateOfLoss" // Keep the same class for styling
              />
            </div>
            <div className="form-group">
              <label className="titleForInformation" htmlFor="insuranceCo">
                Insurance Co:
              </label>
              <input className="insuranceCo" name="insuranceCo" value={formData?.insuranceCo || ""} onChange={handleChange} placeholder="Company" />
            </div>
            <div className="form-group">
              <label className="titleForInformation" htmlFor="claimNumber">
                Claim Number & Type
              </label>
              <input className="claimNumber" type="text" name="claimNumber" value={formData?.claimNumber || ""} onChange={handleChange} placeholder="99-999999999-99" />
              <select className="type" name="claimType" value={formData?.claimType || ""} onChange={handleChange}>
                <option className="dropddownOptions" value="First Party Claim">
                  First Party Claim
                </option>
                <option className="dropddownOptions" value="Third Party Claim">
                  Third Party Claim
                </option>
              </select>
            </div>
          </form>
        </section>

        <section className="form-section">
          <form>
            <h2 className="section-heading">Vehicle Information</h2>
            <div className="form-group">
              <label htmlFor="vin">VIN</label>
              <input className="vin" type="text" name="vin" value={formData?.vin || ""} onChange={handleChange} placeholder="99999999999999999" maxLength={17} />
            </div>
            <div className="form-group">
              <label htmlFor="yearMakeModel">Year, Make & Model</label>
              <input className="yearMakeModel" type="text" name="yearMakeModel" value={formData?.yearMakeModel || ""} onChange={handleChange} placeholder="Year, make & model" />
            </div>
            <div className="form-group">
              <label htmlFor="odometerReading">Odometer</label>
              <NumericFormat
                className="odometer"
                name="odometerReading"
                value={formData?.odometerReading || ""}
                onChange={handleChange}
                onValueChange={(values) => {
                  const { value } = values;
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    odometerReading: value, // Salva o valor numérico sem formatação
                  }));
                }}
                thousandSeparator={","}
                fixedDecimalScale={true}
                placeholder="Odometer Reading"
                allowNegative={false} // Evita valores negativos, se necessário
              />
            </div>
          </form>
        </section>

        <section className="form-section">
          <h2>Damage / Repair Area</h2>
          <div className="car-container">
            <img src="/pngfind.com-car-top-view-png-750271 (1) 1.png" alt="Car" className="car-image" />
            <input type="checkbox" className="damage-checkbox" name="checkbox1" style={{ top: "15%", left: "12%" }} checked={checkboxes.checkbox1} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox2" style={{ top: "50%", left: "0%" }} checked={checkboxes.checkbox2} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox3" style={{ top: "85%", left: "12%" }} checked={checkboxes.checkbox3} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox4" style={{ top: "50%", left: "16%" }} checked={checkboxes.checkbox4} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox5" style={{ top: "10%", left: "35%" }} checked={checkboxes.checkbox5} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox6" style={{ top: "50%", left: "50%" }} checked={checkboxes.checkbox6} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox7" style={{ top: "85%", left: "35%" }} checked={checkboxes.checkbox7} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox8" style={{ top: "15%", left: "70%" }} checked={checkboxes.checkbox8} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox9" style={{ top: "85%", left: "70%" }} checked={checkboxes.checkbox9} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox10" style={{ top: "25%", left: "91%" }} checked={checkboxes.checkbox10} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox11" style={{ top: "50%", left: "94%" }} checked={checkboxes.checkbox11} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox12" style={{ top: "75%", left: "91%" }} checked={checkboxes.checkbox12} onChange={handleCheckboxChange} />
            <input type="checkbox" className="damage-checkbox" name="checkbox13" style={{ top: "50%", left: "100%" }} checked={checkboxes.checkbox13} onChange={handleCheckboxChange} />
          </div>

          <div className="checkbox-group">
            <label>
              <input type="checkbox" name="checkboxUnder" checked={checkboxes.checkboxUnder} onChange={handleCheckboxChange} /> Undercarriage
            </label>
            <label>
              <input type="checkbox" name="checkboxPdr" checked={checkboxes.checkboxPdr} onChange={handleCheckboxChange} /> PDR
            </label>
            <label>
              <input type="checkbox" name="checkboxFire" checked={checkboxes.checkboxFire} onChange={handleCheckboxChange} /> Fire Damage
            </label>
            <label>
              <input type="checkbox" name="checkboxOther" checked={checkboxes.checkboxOther} onChange={handleCheckboxChange} /> Other Damage
            </label>
            <label>
              <input type="checkbox" name="checkboxWater" checked={checkboxes.checkboxWater} onChange={handleCheckboxChange} /> Water Damage
            </label>
          </div>
        </section>

        <section className="form-section">
          <h2>Repair Estimate Review</h2>
          <form>
            <label htmlFor="laborHours">Labor Hours</label>
            <input
              className="laborHours"
              type="text" // Use text to prevent automatic number formatting
              name="laborHours"
              value={formData?.laborHours || ""} // Handle undefined cases
              onChange={(e) => {
                let value = e.target.value;

                // Allow only numbers and at most one dot
                if (!/^\d*\.?\d{0,2}$/.test(value)) {
                  return; // Ignore invalid inputs
                }

                handleInputChange({ target: { name: "laborHours", value } });
              }}
              placeholder="Enter labor hours"
            />

            <div className="parentYesOrno">
              <div className="divForCheckboxes">
                <div className="structuralRow">
                  <div className="radio-group">
                    <label className="labelName">Structural Damage</label>
                    <div className="radio-inline">
                      <select
                        className="optionsSD"
                        name="structuralDamage"
                        value={formData.structuralDamage}
                        onChange={handleInputChange} // same as your regular handler
                      >
                        <option value="None Reported">None Reported</option>
                        <option value="Structural Damage Reported">Structural Damage Reported</option>
                        <option value="Body Pull Reported">Body Pull Reported</option>
                        <option value="Frame Damage Reported">Frame Damage Reported</option>
                        <option value="Structural Repair Reported">Structural Repair Reported</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="airbagRow">
                  <div className="dropdown-group">
                    <label className="labelName" htmlFor="airbagDeployment">
                      Airbag Deployment
                    </label>
                    <select name="airbagDeployment" id="airbagDeployment" value={formData?.airbagDeployment || ""} onChange={handleRadioChange} className="optionsSD">
                      <option value="no">None Reported</option>
                      <option value="yes">Airbag Deployment Reported</option>
                    </select>
                  </div>
                </div>

                <div className="priorRow">
                  <div className="dropdown-group">
                    <label className="labelName" htmlFor="priorLoss">
                      Prior Loss or UPD
                    </label>
                    <select name="priorLoss" id="priorLoss" value={formData?.priorLoss || ""} onChange={(e) => setFormData({ ...formData, priorLoss: e.target.value })} className="optionsSD">
                      <option value="no">None reported</option>
                      <option value="yes">Prior Loss Reported</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="repairCost">Repair Cost</label>
              <NumericFormat
                className="laborHours"
                name="repairCost"
                value={formData?.repairCost}
                onValueChange={(values) => {
                  const { value } = values;
                  handleInputChange({ target: { name: "repairCost", value } });
                }}
                thousandSeparator={","}
                decimalSeparator={"."}
                decimalScale={0}
                allowNegative={false}
                prefix="$"
                placeholder="$999"
              />
            </div>
          </form>
        </section>

        <section className="form-section">
          <h2>Third Party Valuation Data</h2>
          <form className="square-upload">
            <div className={`file-upload ${isDragging ? "dragging" : ""}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} style={{ marginBottom: "20px" }}>
              {/* File input for uploading new files */}
              <input
                type="file"
                id="uploadBlackBook"
                name="uploadBlackBook"
                accept="image/*" // Ensures only images can be selected in the file dialog
                onChange={handleFileChange}
              />
              <label htmlFor="uploadBlackBook" className="file-upload-button">
                Select Files
              </label>

              {/* Error message for invalid file */}
              {errorMessage && <p className="error-message">{errorMessage}</p>}

              {/* Display previously uploaded file */}
              {formData.uploadBlackBook && (
                <div className="uploaded-file-info content-adjustment">
                  <p style={{ marginBottom: "10px" }}>
                    <strong>Uploaded File:</strong>
                  </p>
                  <div className="uploaded-actions">
                    <button type="button" onClick={() => window.open(formData.uploadBlackBook, "_blank")} className="view-button">
                      View File
                    </button>
                    {/* File name */}
                    <span className="file-name">{fileTag}</span>
                  </div>
                </div>
              )}
            </div>
          </form>
        </section>

        <section class="form-section">
          <h2>Market Value Analysis</h2>
          <form class="firstTable">
            <table className="tableContainer">
              <tr>
                <td class="div1">Initial Values</td>
                <td className="div1" style={{ paddingLeft: "80px" }}>
                  High
                </td>
                <td className="div1" style={{ paddingLeft: "80px" }}>
                  Low
                </td>
              </tr>
              <tr>
                <td class="div2">FMV</td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="fmv_high"
                    value={formData?.fmv_high}
                    onValueChange={(values) => {
                      const { value } = values; // Obtém o valor numérico não formatado
                      handleInputChange({
                        target: { name: "fmv_high", value },
                      }); // Usa handleInputChange para atualizar o valor
                    }}
                    thousandSeparator={true} // Adiciona o separador de milhar
                    prefix="$" // Prefixo de dólar
                    placeholder="$99,999.00"
                    decimalScale={0} // Duas casas decimais
                    allowNegative={false} // Não permite valores negativos
                    allowLeadingZeros={false} // Não permite zeros à esquerda
                  />
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="fmv_low"
                    value={formData?.fmv_low}
                    onValueChange={(values) => {
                      const { value } = values; // Obtém o valor numérico não formatado
                      handleInputChange({ target: { name: "fmv_low", value } }); // Usa handleInputChange para atualizar o valor
                    }}
                    thousandSeparator="," // Adiciona o separador de milhar
                    prefix="$" // Adiciona o símbolo de dólar
                    placeholder="$99,999" // Placeholder formatado
                    decimalScale={0} // Sem casas decimais
                    allowNegative={false} // Não permite valores negativos
                    allowLeadingZeros={false} // Não permite zeros à esquerda
                  />
                </td>
              </tr>

              <tr class="">
                <td class="div1" colspan="3">
                  Value Adjustments
                </td>
              </tr>
              <tr class="">
                <td className="div2">
                  <span className="letters">A -</span>
                  <select className="options" name="a_type" value={formData?.a_type} onChange={handleInputChange}>
                    <option value="N/A">N/A</option>
                    <option value="Equipment">Equipment</option>
                    <option value="Mileage">Mileage</option>
                  </select>
                  <select className="signSelectA" name="a_sign" value={formData?.a_sign || "+"} onChange={handleInputChange}>
                    <option value="+">+</option>
                    <option value="-">−</option>
                  </select>
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="a_high"
                    value={formData?.a_high === 0 ? "" : formData?.a_high}
                    onValueChange={(values) => {
                      const { value } = values;
                      handleInputChange({ target: { name: "a_high", value } });
                    }}
                    thousandSeparator=","
                    prefix="$"
                    placeholder="$99,999"
                    decimalScale={0}
                    allowNegative={false}
                    allowLeadingZeros={false}
                  />
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="a_low"
                    value={formData.a_low ? Math.round(formData.a_low) : ""}
                    onValueChange={(values) => {
                      const { value } = values;
                      handleInputChange({ target: { name: "a_low", value } });
                    }}
                    thousandSeparator=","
                    prefix="$"
                    placeholder="$99,999"
                    decimalScale={0}
                    allowNegative={false}
                    allowLeadingZeros={false}
                    readOnly
                  />
                </td>
              </tr>
              <tr>
                <td className="div2">
                  <span className="letters">B -</span>
                  <select className="options" name="b_type" value={formData?.b_type} onChange={handleInputChange}>
                    <option value="N/A">N/A</option>
                    <option value="UPD">UPD</option>
                    <option value="Equipment">Equipment</option>
                    <option value="Prior Loss">Prior Loss</option>
                    <option value="Market">Market</option>
                    <option value="Condition">Condition</option>
                    <option value="Date of Loss">Date of Loss</option>
                    <option value="Mileage">Mileage</option>
                    <option value="Other">Other</option>
                  </select>
                  <select className="signSelectB" name="b_sign" value={formData?.b_sign || "+"} onChange={handleInputChange}>
                    <option value="+">+</option>
                    <option value="-">−</option>
                  </select>
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="b_high"
                    value={formData?.b_high === 0 ? "" : formData?.b_high} // Exibe valor vazio se for 0
                    onValueChange={(values) => {
                      const { value } = values; // Obtém o valor numérico não formatado
                      handleInputChange({ target: { name: "b_high", value } }); // Usa handleInputChange para atualizar o valor
                    }}
                    thousandSeparator=","
                    prefix="$"
                    placeholder="$99,999"
                    decimalScale={0}
                    allowNegative={false}
                    allowLeadingZeros={false}
                  />
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="b_low"
                    value={formData.b_low !== undefined && formData.b_low !== null ? Math.round(formData.b_low) : ""}
                    onValueChange={({ value }) => handleInputChange({ target: { name: "b_low", value } })}
                    thousandSeparator=","
                    prefix="$"
                    placeholder="$99,999"
                    decimalScale={0}
                    allowNegative={false}
                    allowLeadingZeros={false}
                  />
                </td>
              </tr>
              <tr>
                <td className="div2">
                  <span className="letters">C -</span>
                  <select className="options" name="c_type" value={formData?.c_type} onChange={handleInputChange}>
                    <option value="N/A">N/A</option>
                    <option value="UPD">UPD</option>
                    <option value="Equipment">Equipment</option>
                    <option value="Prior Loss">Prior Loss</option>
                    <option value="Market">Market</option>
                    <option value="Condition">Condition</option>
                    <option value="Date of Loss">Date of Loss</option>
                    <option value="Mileage">Mileage</option>
                    <option value="Other">Other</option>
                  </select>
                  <select className="signSelectC" name="c_sign" value={formData?.c_sign || "+"} onChange={handleInputChange}>
                    <option value="+">+</option>
                    <option value="-">−</option>
                  </select>
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="c_high"
                    value={formData?.c_high === 0 ? "" : formData?.c_high} // Exibe valor vazio se for 0
                    onValueChange={(values) => {
                      const { value } = values; // Obtém o valor numérico não formatado
                      handleInputChange({ target: { name: "c_high", value } }); // Usa handleInputChange para atualizar o valor
                    }}
                    thousandSeparator="," // Adiciona o separador de milhar
                    prefix="$" // Adiciona o símbolo de dólar
                    placeholder="$99,999" // Placeholder formatado
                    decimalScale={0} // Não permite casas decimais
                    allowNegative={false} // Não permite valores negativos
                    allowLeadingZeros={false} // Não permite zeros à esquerda
                  />
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="c_low"
                    value={formData.c_low !== undefined && formData.c_low !== null ? Math.round(formData.c_low) : ""}
                    onValueChange={({ value }) => handleInputChange({ target: { name: "c_low", value } })}
                    thousandSeparator=","
                    prefix="$"
                    placeholder="$99,999"
                    decimalScale={0}
                    allowNegative={false}
                    allowLeadingZeros={false}
                  />
                </td>
              </tr>
              <tr class="">
                <td class="div1" colspan="3">
                  Final Values
                </td>
              </tr>
              <tr>
                <td class="div2"></td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="final_high"
                    value={formData.final_high ? parseFloat(formData.final_high).toFixed(0) : ""} // Formata o valor com duas casas decimais
                    thousandSeparator="," // Adiciona o separador de milhar
                    prefix="$" // Adiciona o símbolo de dólar
                    placeholder="$99,999" // Placeholder formatado
                    decimalScale={0} // Exibe sempre duas casas decimais
                    readOnly // Impede a edição direta do campo
                  />
                </td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="final_low"
                    value={formData.final_low ? parseFloat(formData.final_low).toFixed(0) : ""} // Formata o valor com duas casas decimais
                    thousandSeparator="," // Adiciona o separador de milhar
                    prefix="$" // Adiciona o símbolo de dólar
                    placeholder="$99,999" // Placeholder formatado
                    decimalScale={0} // Exibe sempre duas casas decimais
                    readOnly // Impede a edição direta do campo
                  />
                </td>
              </tr>
              <tr class="">
                <td class="div1" colspan="3">
                  Maximum Loss Range
                </td>
              </tr>
              <tr>
                <td class="div2"></td>
                <td class="div2"></td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="final_high"
                    value={formData.max_loss ? parseFloat(formData.max_loss).toFixed(0) : ""} // Formata o valor com duas casas decimais
                    thousandSeparator="," // Adiciona o separador de milhar
                    prefix="$" // Adiciona o símbolo de dólar
                    placeholder="$99,999" // Placeholder formatado
                    decimalScale={0} // Exibe sempre duas casas decimais
                    readOnly // Impede a edição direta do campo
                  />
                </td>
              </tr>
              <tr class="">
                <td class="div1" colspan="3">
                  Total Loss Threshold
                </td>
              </tr>
              <tr>
                <td class="div2"></td>
                <td class="div2"></td>

                <td className="div2">
                  <NumericFormat
                    className="inputTable"
                    name="final_low"
                    value={formData.loss_thresold ? parseFloat(formData.loss_thresold).toFixed(0) : ""} // No decimals
                    thousandSeparator="," // Adds commas for thousands
                    prefix="$" // Adds dollar symbol
                    placeholder="$99,999" // Shows formatted placeholder
                    decimalScale={0} // Ensures no decimal places
                    readOnly // Sets field as read-only
                  />
                </td>
              </tr>
            </table>
          </form>
        </section>

        <section className="form-section" style={{ marginBottom: "100px" }}>
          <h2>Damage Analysis</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="damageValue">Damage to Value</label>
              <input className="damageValue" type="text" id="damageValue" placeholder="99%" value={formData.damage_to_value ? formData.damage_to_value + "%" : ""} readOnly />
            </div>
            <div className="form-group">
              <label htmlFor="damageScore">Damage Severity Score & Grade</label>
              <input
                className="damageScore"
                type="text"
                id="damageScore"
                placeholder="99"
                value={formData.damageScore || ""}
                onChange={(e) => handleDamageScoreChange(e.target.value)} // Correctly call the function here
              />
              <input className="grade" type="text" id="grade" placeholder="9" value={formData.grade || ""} readOnly />
            </div>
          </form>
        </section>

        <section className="form-section">
          <h2>Vehicle Values Chart</h2>
          <div className="chart-wrapper">
            <ChartComponent
              finalHighValue={parseFloat(formData.final_high) || 0}
              postRepairFMV={parseFloat(formData.postRepairFMV) || 0}
              diminishedValue={parseFloat(formData.diminishedValue) || 0}
              setChartImage={setChartImage}
              textColor="#000" // Cor do texto branca para o formulário
            />
          </div>
        </section>

        <section className="form-section">
          <h2>Diminished Value Summary</h2>
          <form>
            <table className="finalTableContainer">
              <tbody>
                <tr className="firstDmsDivContainer">
                  <td className="firstDmsDiv">Pre-Loss FMV</td>
                </tr>
                <tr className="firstDmsDivContainer">
                  <td className="secondDmsDivContainer">
                    <input
                      className="finalDisplay"
                      type="text"
                      name="PreLossFMV"
                      placeholder="$99,999"
                      value={formData.PreLossFMV ? `$${Number(formData.PreLossFMV).toLocaleString("en-US")}` : ""}
                      readOnly
                    />
                  </td>
                </tr>

                <tr className="firstDmsDivContainer">
                  <td className="firstDmsDiv">Maximum DV</td>
                </tr>
                <tr className="firstDmsDivContainer">
                  <td className="secondDmsDivContainer">
                    <input
                      className="finalDisplay"
                      type="text"
                      name="final_high"
                      placeholder="$99,999"
                      value={formData.max_loss ? `$${Math.round(Number(formData.max_loss)).toLocaleString("en-US")}` : ""}
                      readOnly
                    />
                  </td>
                </tr>

                <tr className="firstDmsDivContainer">
                  <td className="firstDmsDiv">Inherent DV</td>
                </tr>
                <tr className="firstDmsDivContainer">
                  <td className="secondDmsDivContainer">
                    <input
                      className="finalDisplay"
                      type="text"
                      name="inherentDV"
                      placeholder="$99,999"
                      value={formData.inherentDV ? `$${Math.round(Number(formData.inherentDV)).toLocaleString("en-US")}` : ""}
                      readOnly
                    />
                  </td>
                </tr>

                <tr className="firstDmsDivContainer">
                  <td className="firstDmsDiv">Repair Related DV</td>
                </tr>
                <tr className="firstDmsDivContainer">
                  <td className="secondDmsDivContainer">
                    <input className="finalDisplay" type="text" name="final_high" placeholder="$99,999" value="0" readOnly />
                  </td>
                </tr>

                <tr className="firstDmsDivContainer">
                  <td className="firstDmsDiv">Post Repair FMV</td>
                </tr>
                <tr className="firstDmsDivContainer">
                  <td className="secondDmsDivContainer">
                    <input
                      className="finalDisplay"
                      type="text"
                      name="postRepairFMV"
                      placeholder="$99,999"
                      value={formData.postRepairFMV ? `$${Math.round(Number(formData.postRepairFMV)).toLocaleString("en-US")}` : ""}
                      readOnly
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </form>
        </section>
      </div>
      <footer>
        <h2>Compliance Statements</h2>

        <div className="personal">
          <label class="inspection-label">A personal inspection of the properties that are the subject of this report was performed.</label>
          <label class="checkbox-label">
            <input type="checkbox" class="custom-checkbox" name="checkboxYes" checked={checkboxes.checkboxYes} onChange={handleCheckboxChange} />
            <span class="checkbox-text">
              <div className="yesText">Yes</div>
            </span>
          </label>
        </div>

        {/*<input type="checkbox" name="checkboxOther" checked={checkboxes.checkboxOther} onChange={handleCheckboxChange}/> Other Damage*/}

        {showPreview && pdfBlob && pdfUrl && (
          <div className="pdf-preview-modal">
            <div className="pdf-preview-content">
              <div className="vin-name">VIN: {formData.vin ? formData.vin : "N/A"}</div>
              <iframe
                src={pdfUrl} // Use the memoized PDF URL
                width="100%"
                height="900px"
                title="PDF Preview"
              ></iframe>
              <div className="button-group">
                <button className="open-button" onClick={() => window.open(pdfUrl, "_blank")}>
                  Open in a new tab
                </button>
                <button
                  className="copy-link-button"
                  onClick={() => {
                    navigator.clipboard.writeText(pdfUrl);
                    alert("Link copied to clipboard!");
                  }}
                >
                  Copy link
                </button>
                <button
                  className="download-button"
                  onClick={() => {
                    const a = document.createElement("a");
                    a.href = pdfUrl;
                    a.download = formData.vin ? `${formData.vin}.pdf` : "AppraisalReport.pdf";
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                >
                  Download
                </button>
                <button className="close-button" onClick={closePreview}>
                  X
                </button>
              </div>
            </div>
          </div>
        )}
      </footer>
      <div className="dv-total">
        <div className="total-dv-2000-container">
          <p>
            <strong>Total DV:</strong> {formData.diminishedValue ? `$${formData.diminishedValue}` : "0"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EditForm;
